import type { ComponentType, ReactNode } from 'react';
import { Briefcase, Check, Handshake, Warning } from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../cx';
import { TONE_DOT, TONE_SOFT, type Tone } from '../tone';
import { Icon } from '../Icon';

/** Chip tones are the shared status vocabulary (ui/tone.ts). */
export type ChipTone = Tone;

/** The strong colour of a tone, for a status dot outside a chip (a kanban lane header). */
export const chipDotClass = (tone: ChipTone): string => TONE_DOT[tone];

/** Pipeline stages by meaning: in progress is info, a good outcome positive, a rejection attention. */
export const STAGE_TONE = {
  saved: 'neutral',
  applied: 'info',
  interviewing: 'info',
  offer: 'positive',
  hired: 'positive',
  rejected: 'attention',
} as const satisfies Record<string, ChipTone>;

/**
 * Offer and hired are good outcomes, but the positive tone's check reads as "selected" in a menu or a stage
 * picker, so these two stages carry their own glyphs. Other stages use the tone's default.
 */
export const STAGE_ICON = { offer: Handshake, hired: Briefcase } as const satisfies Record<
  string,
  ComponentType<IconProps>
>;

/** Positive and attention carry no hue of their own (good shares the selection green, attention is ink), so they carry a glyph. */
const TONE_GLYPH: Partial<Record<ChipTone, ComponentType<IconProps>>> = { positive: Check, attention: Warning };

export type ChipProps = {
  tone?: ChipTone;
  /** One to three words. The word carries the status; colour is only a scanning aid. */
  children: ReactNode;
  /** Defaults to a check (positive) or a warning triangle (attention); the dot is then dropped. */
  icon?: ComponentType<IconProps>;
  /** A leading dot in the tone's strong colour (pipeline stage lists). */
  dot?: boolean;
  className?: string;
};

/** A small read-only status or tag label. Never clickable: tappable values are FilterChip or FilterPill. */
export function Chip({ tone = 'neutral', children, icon: iconProp, dot, className }: ChipProps) {
  const icon = iconProp ?? TONE_GLYPH[tone];
  return (
    <span
      className={cx(
        'inline-flex h-6 max-w-full items-center gap-1 rounded-jc-info px-2.5 text-xs font-semibold whitespace-nowrap',
        TONE_SOFT[tone],
        className,
      )}
    >
      {dot && !icon ? <span aria-hidden className={cx('size-1.5 shrink-0 rounded-full', TONE_DOT[tone])} /> : null}
      {icon ? <Icon icon={icon} size="sm" /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
}
